/**
 * WHAT: Login page component for DAMA application authentication.
 * WHY: Provides the entry point for users to authenticate via username/password
 *      before accessing the Database Access Management & Attestation platform.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from '../context/AuthContext';

import API_CONFIG from '../config/api.config';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Login form component that authenticates users against the DAMA backend.
 * WHY: Gate access to the platform so only authorized personnel can manage
 *      database access reviews, attestations, and compliance operations.
 */
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('auth/login/'), {
        method: 'POST',
        // Include credentials so the server-side session cookie set on login is stored.
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success) {
        // Update auth context (which also stores in localStorage)
        login(data.user);

        // Redirect based on role
        if (data.user.role.can_manage_campaigns) {
          navigate('/');
        } else if (data.user.role.can_perform_attestations) {
          navigate('/');
        } else {
          navigate('/');
        }
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-5">
            <div className="card shadow-lg border-0" style={{ borderRadius: '15px' }}>
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <div className="mb-3" style={{ position: 'relative', display: 'inline-block' }}>
                    <i className="fas fa-shield-alt" style={{ fontSize: '3.5rem', color: '#4fd1c5' }}></i>
                    <i className="fas fa-database" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: '1.5rem',
                      color: '#2d3748'
                    }}></i>
                  </div>
                  <h2 className="fw-bold" style={{ color: '#2d3748', letterSpacing: '0.15em' }}>D A M A</h2>
                  <p className="text-muted">Database Access Management & Attestation</p>
                </div>

                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="username" className="form-label fw-semibold">
                      <i className="fas fa-user me-2"></i>Username
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      id="username"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoFocus
                      disabled={loading}
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="password" className="form-label fw-semibold">
                      <i className="fas fa-lock me-2"></i>Password
                    </label>
                    <input
                      type="password"
                      className="form-control form-control-lg"
                      id="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-lg w-100 text-white"
                    disabled={loading}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Signing in...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Sign In
                      </>
                    )}
                  </button>
                </form>

              </div>
            </div>

            <div className="text-center mt-3 text-white">
              <small>
                <i className="fas fa-lock me-1"></i>
                Secure Access Portal
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
